import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCoachAnalytics } from '@/hooks/useCoachAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Users, Calendar, Star, Clock, TrendingUp, Activity, UserCheck, XCircle,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, BarChart, Bar,
} from 'recharts';
import { format, subDays, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';

type RangeKey = '7d' | '30d' | '90d' | '12m';

const RANGE_KEYS: { key: RangeKey; labelKey: string }[] = [
  { key: '7d', labelKey: 'coachStats.range7d' },
  { key: '30d', labelKey: 'coachStats.range30d' },
  { key: '90d', labelKey: 'coachStats.range90d' },
  { key: '12m', labelKey: 'coachStats.range12m' },
];

function getRange(key: RangeKey): { start: string; end: string } {
  const end = new Date();
  let start: Date;
  switch (key) {
    case '7d':  start = subDays(end, 6); break;
    case '30d': start = subDays(end, 29); break;
    case '90d': start = subDays(end, 89); break;
    case '12m': start = subMonths(end, 12); break;
  }
  const toISO = (d: Date) => d.toISOString().slice(0, 10);
  return { start: toISO(start), end: toISO(end) };
}

const DOW_LABEL_KEYS = ['dow.mon', 'dow.tue', 'dow.wed', 'dow.thu', 'dow.fri', 'dow.sat', 'dow.sun'];

interface Props {
  coachUserId: string | undefined;
}

const KpiCard: React.FC<{ icon: React.ElementType; label: string; value: string | number; hint?: string }> = ({
  icon: Icon, label, value, hint,
}) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground truncate">{label}</p>
          <p className="text-2xl font-bold tabular-nums">{value}</p>
          {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
        </div>
        <div className="rounded-full bg-primary/10 p-2 shrink-0">
          <Icon className="h-4 w-4 text-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const CoachStatsTab: React.FC<Props> = ({ coachUserId }) => {
  const { t } = useTranslation('adminui');
  const DOW_LABELS = DOW_LABEL_KEYS.map((k) => t(k));
  const RANGES = RANGE_KEYS.map((r) => ({ key: r.key, label: t(r.labelKey) }));
  const [rangeKey, setRangeKey] = useState<RangeKey>('30d');
  const { start, end } = useMemo(() => getRange(rangeKey), [rangeKey]);
  const { data, isLoading, isError, refetch } = useCoachAnalytics(coachUserId, start, end);

  const heatmapMatrix = useMemo(() => {
    // 7 rows (dow 1..7) × 24 cols (hour 0..23) of booking counts
    const m: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    let max = 0;
    (data?.heatmap ?? []).forEach((h) => {
      const r = (h.dow - 1) % 7;
      const c = Math.max(0, Math.min(23, h.hour));
      m[r][c] += h.bookings ?? 0;
      if (m[r][c] > max) max = m[r][c];
    });
    return { m, max };
  }, [data]);

  if (!coachUserId) {
    return (
      <div className="text-sm text-muted-foreground p-4">
        {t('coachStats.noLinkedAccount')}
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Range selector */}
      <div className="flex flex-wrap gap-2">
        {RANGES.map((r) => (
          <Button
            key={r.key}
            size="sm"
            variant={rangeKey === r.key ? 'default' : 'outline'}
            onClick={() => setRangeKey(r.key)}
          >
            {r.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="py-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">{t('coachStats.loadError')}</p>
            <Button size="sm" onClick={() => refetch()}>{t('coachStats.retry')}</Button>
          </CardContent>
        </Card>
      ) : !data?.kpis || data.kpis.total_bookings === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t('coachStats.noDataPeriod')}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard icon={Calendar} label={t('coachStats.sessionsCoached')} value={data.kpis.sessions_coached} />
            <KpiCard icon={Users} label={t('coachStats.distinctMembers')} value={data.kpis.distinct_members} />
            <KpiCard icon={Activity} label={t('coachStats.bookings')} value={data.kpis.total_bookings} />
            <KpiCard icon={TrendingUp} label={t('coachStats.fillRate')} value={`${data.kpis.avg_fill_rate}%`} />
            <KpiCard icon={Clock} label={t('coachStats.estimatedWorkHours')} value={`${data.kpis.estimated_work_hours}h`} />
            <KpiCard
              icon={Star}
              label={t('coachStats.avgRating')}
              value={data.kpis.avg_rating != null ? data.kpis.avg_rating.toFixed(2) : '—'}
              hint={t('coachStats.totalRatings', { count: data.kpis.total_ratings })}
            />
            <KpiCard icon={XCircle} label={t('coachStats.cancellationRate')} value={`${data.kpis.cancellation_rate}%`} />
            <KpiCard
              icon={UserCheck}
              label={t('coachStats.retention')}
              value={`${data.kpis.retention_rate}%`}
              hint={t('coachStats.retentionHint')}
            />
          </div>

          {/* Heatmap */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">{t('coachStats.heatmapTitle')}</CardTitle>
              <p className="text-xs text-muted-foreground">
                {t('coachStats.heatmapSubtitle')}
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <div className="inline-block min-w-full">
                  <div className="grid grid-cols-[40px_repeat(24,minmax(18px,1fr))] gap-px text-[9px]">
                    <div />
                    {Array.from({ length: 24 }).map((_, h) => (
                      <div key={h} className="text-center text-muted-foreground">
                        {h % 3 === 0 ? h : ''}
                      </div>
                    ))}
                    {heatmapMatrix.m.map((row, r) => (
                      <React.Fragment key={r}>
                        <div className="text-muted-foreground pr-1 text-right self-center">{DOW_LABELS[r]}</div>
                        {row.map((v, c) => {
                          const intensity = heatmapMatrix.max > 0 ? v / heatmapMatrix.max : 0;
                          const bg = v === 0 ? 'hsl(var(--muted))' : `hsl(var(--primary) / ${0.15 + intensity * 0.85})`;
                          return (
                            <div
                              key={c}
                              className="h-5 rounded-sm"
                              style={{ backgroundColor: bg }}
                              title={t('coachStats.heatmapCellTitle', { dow: DOW_LABELS[r], hour: c, count: v })}
                            />
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Work schedule per dow */}
          {data.workSchedule.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('coachStats.workScheduleTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                  {data.workSchedule.map((w) => (
                    <div key={w.dow} className="rounded-lg border p-2 text-center">
                      <div className="text-xs font-semibold">{DOW_LABELS[(w.dow - 1) % 7]}</div>
                      <div className="text-sm tabular-nums mt-1">
                        {String(w.first_hour).padStart(2, '0')}h–{String(w.last_hour).padStart(2, '0')}h
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {t('coachStats.sessionsAndHours', { count: w.sessions_count, hours: w.total_hours })}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Bookings timeline */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('coachStats.bookingsEvolution')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.bookingsTimeline}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis
                        dataKey="d"
                        tickFormatter={(v) => format(new Date(v), 'd MMM', { locale: fr })}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip
                        labelFormatter={(v) => format(new Date(v as string), 'PPP', { locale: fr })}
                        contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="bookings"
                        stroke="hsl(var(--primary))"
                        fill="hsl(var(--primary) / 0.2)"
                        name={t('coachStats.bookings')}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Ratings timeline */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('coachStats.ratingEvolution')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  {data.ratingsTimeline.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                      {t('coachStats.noRatingsPeriod')}
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={data.ratingsTimeline}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis
                          dataKey="month"
                          tickFormatter={(v) => format(new Date(v), 'MMM yy', { locale: fr })}
                          tick={{ fontSize: 10 }}
                        />
                        <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                        <Tooltip
                          labelFormatter={(v) => format(new Date(v as string), 'MMMM yyyy', { locale: fr })}
                          contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                        />
                        <Line type="monotone" dataKey="avg_rating" stroke="hsl(var(--primary))" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Top courses */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('coachStats.topCoursesTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.topCourses.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('coachStats.noCoursesPeriod')}</p>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.topCourses} layout="vertical" margin={{ left: 24 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis dataKey="course_name" type="category" tick={{ fontSize: 10 }} width={100} />
                        <Tooltip
                          contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                        />
                        <Bar dataKey="bookings" fill="hsl(var(--primary))" name={t('coachStats.bookings')} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top members */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('coachStats.topMembersTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.topMembers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('coachStats.noMembersPeriod')}</p>
                ) : (
                  <ul className="space-y-2">
                    {data.topMembers.map((m) => {
                      const name = `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || t('coachStats.member');
                      const initials = `${m.first_name?.[0] ?? ''}${m.last_name?.[0] ?? ''}`.toUpperCase() || '?';
                      return (
                        <li key={m.user_id} className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Avatar className="h-8 w-8">
                              {m.profile_photo_url ? <AvatarImage src={m.profile_photo_url} /> : null}
                              <AvatarFallback>{initials}</AvatarFallback>
                            </Avatar>
                            <span className="text-sm truncate">{name}</span>
                          </div>
                          <Badge variant="secondary" className="shrink-0">{t('coachStats.sessionsCount', { count: m.bookings })}</Badge>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default CoachStatsTab;
